import React from 'react';
import { Dialog, DialogTitle, DialogActions, Button, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  subtitle: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  confirmButtonColor?: 'blue' | 'green' | 'gray' | 'red';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  subtitle,
  confirmText = 'Save',
  cancelText = 'Cancel',
  isLoading = false,
  confirmButtonColor = 'blue',
}) => {
  const getConfirmButtonClass = () => {
    switch (confirmButtonColor) {
      case 'green':
        return 'bg-green-600 hover:bg-green-700 text-white';
      case 'gray':
        return 'bg-gray-600 hover:bg-gray-700 text-white';
      case 'red':
        return 'bg-red-600 hover:bg-red-700 text-white';
      default:
        return 'bg-blue-600 hover:bg-blue-700 text-white';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            margin: { xs: 2, sm: 'auto' },
            maxWidth: { xs: 'calc(100% - 32px)', sm: 700 },
            width: { xs: '100%', sm: 'auto' },
          },
        },
      }}
    >
      <div className="bg-background-surface/95 backdrop-blur-sm border border-gray-600 rounded-lg">
        {/* Header with title and X button inline */}
        <DialogTitle className="flex items-center justify-between text-white p-6 pb-4">
          <h2 className="text-lg font-bold pr-4">{title}</h2>
          <IconButton
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors flex-shrink-0 hover:bg-transparent"
            size="small"
            sx={{ '&:hover': { backgroundColor: 'transparent' } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        {/* Full width divider with more spacing */}
        <div className="border-b border-gray-600 mx-0"></div>

        {/* Subtitle with increased spacing */}
        <div className="px-6 py-6">
          <p className="text-text-secondary text-sm leading-relaxed">{subtitle}</p>
        </div>

        {/* Action buttons with more padding, no divider */}
        <DialogActions className="p-6 pt-0">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              onClick={onClose}
              className="hover:bg-gray-700/50 transition-colors order-2 sm:order-1 px-6 py-2 rounded-lg"
              disabled={isLoading}
              fullWidth={false}
              sx={{ color: '#9ca3af' }}
            >
              {cancelText}
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isLoading}
              className={`${getConfirmButtonClass()} hover:bg-opacity-80 transition-colors order-1 sm:order-2 px-6 py-2 rounded-lg`}
              fullWidth={false}
              sx={{ color: '#ffffff' }}
            >
              {isLoading ? 'Saving...' : confirmText}
            </Button>
          </div>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default ConfirmationModal;
